import {
    ActionStateValues,
    ButtonType,
    ColorsType,
    PerthNowColourMap,
    TheWestColourMap,
    tokens,
} from '@news-mono/design-tokens'
import { Product } from '@news-mono/web-common'
import { FallbackImages } from '../__fallback-images'
import { Fonts, fonts } from '../__styling/settings/fonts'
import {
    CardMargins,
    ComponentMetrics,
    metrics,
    SiteMetrics,
    ThemeMargins,
} from '../__styling/settings/metrics'
import { sections } from './settings/sections'
import { PerthNowSectionValues } from './settings/sections/perthnow'
import { SevenNewsSectionValues } from './settings/sections/sevennews'
import { TheWestSectionValues } from './settings/sections/thewest'
import {
    CommonZIndex,
    PerthNowZindex,
    SevenNewsZindex,
    TheWestZindex,
    zIndex,
} from './settings/z-index'

export interface Theme {
    fallbackImages: FallbackImages
    kind: Product
    fonts: Fonts
    margins: ThemeMargins
    cards: CardMargins
    componentMetrics: ComponentMetrics
    siteMetrics: SiteMetrics
    section: string
    sectionValues:
        | TheWestSectionValues
        | PerthNowSectionValues
        | SevenNewsSectionValues
    zIndex: (TheWestZindex | PerthNowZindex | SevenNewsZindex) & CommonZIndex
    baseFontSize: number
    colors: ColorsType
}

type EmotionTheme = Theme

declare module '@emotion/react' {
    export interface Theme extends EmotionTheme {}
}

export const thewest: Theme = {
    kind: Product.TheWest,
    fonts: fonts.thewest,
    margins: metrics.thewest.margins,
    cards: metrics.thewest.cards,
    componentMetrics: metrics.thewest.componentMetrics,
    siteMetrics: metrics.thewest.siteMetrics,
    zIndex: { ...zIndex.thewest, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.thewest.default,
    baseFontSize: metrics.thewest.baseFontSize,
    colors: tokens.thewest.colors,
}

export const perthnow: Theme = {
    kind: Product.PerthNow,
    fonts: fonts.perthnow,
    margins: metrics.perthnow.margins,
    cards: metrics.perthnow.cards,
    componentMetrics: metrics.perthnow.componentMetrics,
    siteMetrics: metrics.perthnow.siteMetrics,
    zIndex: { ...zIndex.perthnow, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.perthnow.default,
    baseFontSize: metrics.perthnow.baseFontSize,
    colors: tokens.perthnow.colors,
}

export const sevennews: Theme = {
    kind: Product.SevenNews,
    fonts: fonts.sevennews,
    margins: metrics.sevennews.margins,
    cards: metrics.sevennews.cards,
    componentMetrics: metrics.sevennews.componentMetrics,
    siteMetrics: metrics.sevennews.siteMetrics,
    zIndex: { ...zIndex.sevennews, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.sevennews.default,
    baseFontSize: metrics.sevennews.baseFontSize,
    colors: tokens.sevennews.colors,
}

export const themes = [thewest, perthnow, sevennews]

export interface ThemeProps {
    theme: Theme
}

export type ThemeOverrideTypes =
    | 'tokyo'
    | 'grand-final'
    | 'cnbc'
    | 'queens-legacy'
export type ProductTypes = 'perthnow' | 'thewest'

interface getThemeOverrideValuesProps {
    override: ThemeOverrideTypes
    product: Product
}

export function getThemeOverrideValues({
    override,
    product,
}: getThemeOverrideValuesProps) {
    switch (override) {
        case 'grand-final':
            return getGrandFinalThemeValues({ product })
        case 'cnbc':
            return getCNBCThemeValues() //This is only active on The West
        case 'queens-legacy':
            return getQueensLegacyThemeValues()
        default:
            return {}
    }
}

interface featureTheme {
    sectionValues: any
    colors: {
        background: {
            primary: string
            secondary: string
        }
        brand: string
        text: any
        actions: {
            button: ButtonType
            link: ActionStateValues
        }
        borders: any
        icons: {
            default: string
            hover: string
        }
    }
}

function getCNBCThemeValues(): featureTheme {
    //We are using almost all the colors inside the Grand Final Theme, aside from the primary background.
    const theme = tokens['thewest'].colors as TheWestColourMap
    const palette = theme.palette.grandFinal
    const cnbcPalette = theme.palette.cnbc

    return {
        sectionValues: { primaryColor: palette.lightBlue },
        colors: {
            background: {
                primary: cnbcPalette.darkBlue,
                secondary: cnbcPalette.darkBlue,
            },
            brand: palette.white,
            text: {
                primary: palette.white,
                secondary: palette.lightBlue,
                tertiary: palette.darkBlue,
                heading: palette.white,
            },
            actions: {
                button: {
                    primary: {
                        background: {
                            default: palette.lightBlue,
                            hover: palette.transparent,
                            active: palette.transparent,
                            focus: palette.transparent,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.lightBlue,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                        border: {
                            default: palette.white,
                            hover: palette.lightBlue,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                    },
                    secondary: {
                        background: {
                            default: palette.gradientBlue,
                            hover: palette.white,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.white,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                        border: {
                            default: palette.lightBlue,
                            hover: palette.white,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                    },
                },
                link: {
                    default: palette.white,
                    active: palette.lightBlue,
                    focus: palette.white,
                    hover: palette.lightBlue,
                },
            },
            borders: {
                primary: palette.lightBlueBorderTransparent,
                secondary: cnbcPalette.darkBlue,
            },
            icons: {
                default: palette.lightBlue,
                hover: palette.white,
            },
        },
    }
}

function getQueensLegacyThemeValues(): featureTheme {
    const theme = tokens['thewest'].colors as TheWestColourMap
    const palette = theme.palette.queensLegacy

    return {
        sectionValues: { primaryColor: palette.white },
        colors: {
            background: {
                primary: palette.pink,
                secondary: palette.gradientMaroon,
            },
            brand: palette.white,
            text: {
                primary: palette.white,
                secondary: palette.white,
                tertiary: palette.white,
                heading: palette.white,
            },
            actions: {
                button: {
                    primary: {
                        background: {
                            default: palette.maroon,
                            hover: palette.transparent,
                            active: palette.transparent,
                            focus: palette.transparent,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.white,
                            active: palette.white,
                            focus: palette.white,
                        },
                        border: {
                            default: palette.white,
                            hover: palette.white,
                            active: palette.white,
                            focus: palette.white,
                        },
                    },
                    secondary: {
                        background: {
                            default: palette.gradientMaroon,
                            hover: palette.white,
                            active: palette.white,
                            focus: palette.white,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.white,
                            active: palette.white,
                            focus: palette.white,
                        },
                        border: {
                            default: palette.maroon,
                            hover: palette.white,
                            active: palette.white,
                            focus: palette.white,
                        },
                    },
                },
                link: {
                    default: palette.white,
                    active: palette.white,
                    focus: palette.white,
                    hover: palette.white,
                },
            },
            borders: {
                primary: palette.whiteBorderTransparent,
                secondary: palette.maroon,
            },
            icons: {
                default: palette.white,
                hover: palette.white,
            },
        },
    }
}

function getGrandFinalThemeValues({
    product,
}: {
    product: Product
}): featureTheme {
    const theme = tokens[product].colors as TheWestColourMap | PerthNowColourMap
    const palette = theme.palette.grandFinal

    return {
        sectionValues: { primaryColor: palette.lightBlue },
        colors: {
            background: {
                primary: palette.darkBlue,
                secondary: palette.gradientBlue,
            },
            brand: palette.white,
            text: {
                primary: palette.white,
                secondary: palette.lightBlue,
                tertiary: palette.darkBlue,
                heading: palette.white,
            },
            actions: {
                button: {
                    primary: {
                        background: {
                            default: palette.lightBlue,
                            hover: palette.transparent,
                            active: palette.transparent,
                            focus: palette.transparent,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.lightBlue,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                        border: {
                            default: palette.white,
                            hover: palette.lightBlue,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                    },
                    secondary: {
                        background: {
                            default: palette.gradientBlue,
                            hover: palette.white,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.white,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                        border: {
                            default: palette.lightBlue,
                            hover: palette.white,
                            active: palette.lightBlue,
                            focus: palette.lightBlue,
                        },
                    },
                },
                link: {
                    default: palette.white,
                    active: palette.lightBlue,
                    focus: palette.white,
                    hover: palette.lightBlue,
                },
            },
            borders: {
                primary: palette.lightBlueBorderTransparent,
                secondary: palette.lightBlue,
            },
            icons: {
                default: palette.lightBlue,
                hover: palette.white,
            },
        },
    }
}
